import {Action, ActionReducer, createReducer, on} from "@ngrx/store";
import * as init from "./init.actions";
import {InitState} from "./init";
import {initPublicSuccess} from './init.actions';
import {TypedAction} from '@ngrx/store/src/models';
import {Serializer, Serializers} from '../../interfaces/serializer';
import {Site} from '../../interfaces/site';
import {Space} from '../../interfaces/space';
import {User} from '../../interfaces/user';

const reducer: ActionReducer<InitState> = createReducer(
  {
    logged: null,
    user: null,
    sites: [],
    site: null,
    space: null,
    loading: false,
    code: null,
    isSpaceAdmin: false,
    isDatasetAdmin: false,
    isAdmin: false,
    errors: [],
    loaded: false
  } as InitState,
  on(init.logIn, (state: InitState): InitState => {
    return { ...state, logged: false, loading: true };
  }),
  on(init.logInSuccess, (state: InitState): InitState => {
    return { ...state, logged: true, loading: false };
  }),
  on(init.logInFailure, (state: InitState): InitState => {
    return { ...state, logged: false, loading: false, errors: ['loginPage.invalid_email_or_password'] };
  }),
  on(init.logOut, (state: InitState): InitState => {
    return { ...state, loading: true };
  }),
  on(init.logOutSuccess, (): InitState => {
    return {
      logged: false,
      sites: [],
      site: null,
      space: null,
      loading: false,
      code: null,
      isSpaceAdmin: false,
      isDatasetAdmin: false,
      isAdmin: false,
      loaded: false,
      errors: []
    } as InitState;
  }),
  on(init.logOutFailure, (state: InitState): InitState => {
    return { ...state, loading: false };
  }),
  on(init.initApp, (): InitState => {
    return {
      user: null,
      logged: false,
      sites: [],
      site: null,
      space: null,
      loading: true,
      loaded: false,
      errors: []
    } as InitState;
  }),
  on(init.initAppSuccess, (state: InitState, action: TypedAction<string> & {
    sites: Serializers<Site>;
    site: Serializer<Site> | null | undefined;
    space: Serializer<Space> | null | undefined;
    user: Serializer<User>;
  }): InitState => {
    const superAdmin: boolean = ['super-admin'].includes(action.site?.attributes.user_role);
    const datasetAdmin: boolean = ['admin', 'super-admin'].includes(action.site?.attributes.user_role);
    const spaceAdmin: boolean = ['admin', 'super-admin'].includes(action.user?.attributes.space_role);

    return {
      ...state,
      logged: true,
      user: action.user,
      site: action.site,
      sites: action.sites,
      space: action.space,
      loading: false,
      code: 200,
      isDatasetAdmin: datasetAdmin,
      isSpaceAdmin: spaceAdmin,
      isAdmin: datasetAdmin || spaceAdmin,
      isSuperAdmin: superAdmin,
      errors: [],
      loaded: true
    };
  }),
  on(init.initAppFailure, (_: InitState, action: TypedAction<string> & {
    sites: Serializers<Site>;
    site: Serializer<Site> | null | undefined;
    space: Serializer<Space> | null | undefined;
    user: Serializer<User>;
    code: number;
  }): InitState => {
    return {
      logged: action.user && true || false,
      sites: action.sites || [],
      site: action.site || null,
      space: action.space || null,
      user: null,
      loading: false,
      code: action.code,
      isDatasetAdmin: false,
      isSpaceAdmin: false,
      isAdmin: false,
      errors: [],
      loaded: true
    } as InitState;
  }),
  on(init.initSite, (state: InitState): InitState => {
    return { ...state, loading: true };
  }),
  on(init.initSiteSuccess, (state: InitState, action: TypedAction<string> & { site: Serializer<Site>, title: string }): InitState => {
    return {
      ...state,
      site: action.site,
      title: action.title,
      loading: false
    };
  }),
  on(init.initSiteFailure, (state: InitState): InitState => {
    return { ...state, loading: false };
  }),
  on(init.updateSites, (state: InitState): InitState => {
    return { ...state, loading: true };
  }),
  on(init.updateSitesSuccess, (state: InitState, action: TypedAction<string> & { sites: Serializers<Site> }): InitState => {
    return { ...state, sites: [...action.sites], loading: false };
  }),
  on(init.deleteSite, (state: InitState): InitState => {
    return { ...state, loading: true };
  }),
  on(init.deleteSiteSuccess, (state: InitState, action: TypedAction<string> & { site: Serializer<Site> }): InitState => {
    return {
      ...state,
      sites: state.sites.filter((site: Serializer<Site>): boolean => site.id !== action.site.id),
      loading: false
    };
  }),
  on(init.createSite, (state: InitState): InitState => {
    return { ...state, loading: true };
  }),
  on(init.createSiteSuccess, (state: InitState, action: TypedAction<string> & { site: Serializer<Site> }): InitState => {
    return { ...state, sites: [...state.sites, action.site], loading: false };
  }),
  on(init.updateSite, (state: InitState) => {
    return { ...state, loading: true };
  }),
  on(init.updateSiteSuccess, (state: InitState, action: TypedAction<string> & { site: Serializer<Site> }): InitState => {
    Object.assign(state.sites.find((site: Serializer<Site>): boolean => site.id === action.site.id), action.site);

    return {
      ...state,
      loading: false,
      sites: [...state.sites]
    };
  }),
  on(init.updateSiteMatchingsStatus, (state: InitState, action): InitState => {
    state.site.attributes.statuses.matchings = action.status;
    return { ...state };
  }),
  on(init.initPublic, (state: InitState): InitState => {
    return {
      ...state,
      loading: true
    };
  }),
  on(initPublicSuccess, (state: InitState, action: TypedAction<string> & { dataset: Serializer<Site>, space: Serializer<Space> }): InitState => {
    return {
      ...state,
      site: action.dataset,
      space: action.space,
      loading: false,
      loaded: true
    }
  })
);

export const initReducer: ActionReducer<InitState> = (state: InitState, action: Action) => {
  return reducer(state, action);
};
